<template>
  <teleport to="body" :disabled="!transfer">
    <transition
      :name="ns.b('fade')"
      @after-enter="afterEnter"
      @after-leave="afterLeave"
      @before-leave="beforeLeave"
    >
      <sg-overlay
        v-show="visible"
        :mask="mask"
        :z-index="zIndex"
        @click="handleClose"
      >
        <sg-focus-trap
          loop
          :trapped="visible"
          :focus-trap-sg="drawerRef"
          :focus-start-sg="focusStartRef"
          @release-requested="onCloseRequested"
        >
          <div
            ref="drawerRef"
            aria-modal="true"
            :aria-label="title || undefined"
            :aria-labelledby="!title ? titleId : undefined"
            :aria-describedby="bodyId"
            :class="[ns.b(), direction, visible && 'open', customClass]"
            :style="
              isHorizontal ? 'width: ' + drawerSize : 'height: ' + drawerSize
            "
            role="dialog"
            @click.stop
          >
            <span ref="focusStartRef" :class="ns.e('sr-focus')" tabindex="-1" />
            <header v-if="withHeader" :class="ns.e('header')">
              <slot
                v-if="!$slots.title"
                name="header"
                :close="handleClose"
                :title-id="titleId"
                :title-class="ns.e('title')"
              >
                <span
                  v-if="!$slots.title"
                  :id="titleId"
                  role="heading"
                  :class="ns.e('title')"
                >
                  {{ title }}
                </span>
              </slot>
              <slot v-else name="title">
                <!-- DEPRECATED SLOT -->
              </slot>

              <!-- 最大化 -->
              <button
                v-if="maximize"
                aria-label="最大化"
                :class="[ns.em('actions', 'button'), ns.e('maximize')]"
                type="button"
                @click="changeSize()"
              >
                <sg-icon class="maximize">
                  <Maximize v-if="status" />
                  <Checkbox v-else />
                </sg-icon>
              </button>
              <!-- 关闭 -->
              <button
                v-if="closable"
                aria-label="关闭"
                :class="[ns.em('actions', 'button'), ns.e('close')]"
                type="button"
                @click="handleClose"
              >
                <sg-icon class="close">
                  <Close />
                </sg-icon>
              </button>
            </header>
            <template v-if="rendered">
              <div :id="bodyId" :class="ns.e('body')">
                <slot />
              </div>
            </template>
            <div v-if="$slots.footer" :class="ns.e('footer')">
              <slot name="footer" />
            </div>
          </div>
        </sg-focus-trap>
      </sg-overlay>
    </transition>
  </teleport>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import { Close, Checkbox, Maximize } from '@sgui-plus/icons-vue'

import { SgOverlay } from '@sgui-plus/components/overlay'
import { useModal } from '@sgui-plus/components/modal'
import SgIcon from '@sgui-plus/components/icon'
import { TrapFocus } from '@sgui-plus/directives'
import { useNamespace } from '@sgui-plus/hooks'
import { drawerEmits, drawerProps } from './drawer'
import SgFocusTrap from '@sgui-plus/components/focus-trap'


export default defineComponent({
  name: 'SgDrawer',
  components: {
    SgOverlay,
    SgFocusTrap,
    SgIcon,
    Close,
    Checkbox,
    Maximize,
  },
  directives: {
    TrapFocus,
  },
  props: drawerProps,
  emits: drawerEmits,

  setup(props, ctx) {
    const drawerRef = ref<HTMLElement>()
    const focusStartRef = ref<HTMLElement>()
    const ns = useNamespace('drawer')

    const status = ref<Boolean>(false)

    const isHorizontal = computed(
      () => props.direction === 'rtl' || props.direction === 'ltr'
    )
    const drawerSize = computed(() =>{
      if(status.value) {
        return '100%'
      } else {
        return typeof props.size === 'number' ? `${props.size}px` : props.size
      }
    })

    const changeSize = () => {
      if (!props.maximize) return false
      status.value = !status.value
      ctx.emit('maximize', status.value as boolean)
    }

    return {
      ...useModal(props, drawerRef),
      drawerRef,
      focusStartRef,
      isHorizontal,
      drawerSize,
      ns,
      status,
      changeSize
    }
  },
})
</script>
